/**
 * Booking settlement hooks.
 *
 * `useBookingStatement` fetches the consolidated expense statement (the single
 * source of truth for the unified expenses list + the booking debt KPI).
 * `useSettleBookingCharges` pays a selected mix of folio + guest charge lines
 * in one atomic action via `POST /api/v1/bookings/{id}/settle/`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/core';
import { notify } from '@/shared/services';
import {
  getBookingExpenseStatement,
  setFolioChargeBlLink,
  settleBookingCharges,
  settleBookingSplit,
} from '../services/bookingService';
import type { SettleBookingChargesInput, SettleBookingSplitInput } from '../types';

export const bookingStatementKey = (bookingId: string) =>
  ['booking-expense-statement', bookingId] as const;

/** Consolidated expense statement (charges + deposits + payments + totals). */
export function useBookingStatement(bookingId: string | undefined) {
  return useQuery({
    queryKey: bookingStatementKey(bookingId ?? ''),
    queryFn: () => getBookingExpenseStatement(bookingId as string),
    enabled: Boolean(bookingId),
  });
}

/** Pay a selected mix of folio + guest charge lines in one atomic action. */
export function useSettleBookingCharges(bookingId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (input: SettleBookingChargesInput) => settleBookingCharges(bookingId, input),
    onSuccess: () => {
      // The statement drives the unified list + KPI; refresh the folio,
      // guest-charge and booking surfaces too so every view stays in sync.
      queryClient.invalidateQueries({ queryKey: bookingStatementKey(bookingId) });
      queryClient.invalidateQueries({ queryKey: ['folio', bookingId] });
      queryClient.invalidateQueries({ queryKey: ['booking-guest-charges', bookingId] });
      queryClient.invalidateQueries({ queryKey: ['booking-guest-deposits', bookingId] });
      queryClient.invalidateQueries({ queryKey: ['booking', bookingId] });
      notify.success(t('bookingPayment.success', 'Payment recorded.'));
    },
    onError: (error: Error) => {
      notify.error(error.message, {
        description: t('bookingPayment.failed', 'Could not record the payment.'),
      });
    },
  });
}

/** Pay by amount, split between payers, against the folio balance (atomic). */
export function useSettleBookingSplit(bookingId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (input: SettleBookingSplitInput) => settleBookingSplit(bookingId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookingStatementKey(bookingId) });
      queryClient.invalidateQueries({ queryKey: ['folio', bookingId] });
      queryClient.invalidateQueries({ queryKey: ['booking-guest-charges', bookingId] });
      queryClient.invalidateQueries({ queryKey: ['booking-guest-deposits', bookingId] });
      queryClient.invalidateQueries({ queryKey: ['booking', bookingId] });
      notify.success(t('bookingPayment.success', 'Payment recorded.'));
    },
    onError: (error: Error) => {
      notify.error(error.message, {
        description: t('bookingPayment.failed', 'Could not record the payment.'),
      });
    },
  });
}

/**
 * Link / unlink a folio charge from the Bookinglayer outbound push.
 * Unlinking drops the charge from the booking's BL amendment; re-linking
 * adds it back. The local folio balance is untouched either way.
 */
export function useSetFolioChargeBlLink(bookingId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ chargeId, linked }: { chargeId: string; linked: boolean }) =>
      setFolioChargeBlLink(chargeId, linked),
    onSuccess: (_data, { linked }) => {
      queryClient.invalidateQueries({ queryKey: bookingStatementKey(bookingId) });
      queryClient.invalidateQueries({ queryKey: ['folio', bookingId] });
      notify.success(
        linked
          ? t('bookingExpenses.blLinked', 'Charge linked — it will be reflected in Bookinglayer.')
          : t('bookingExpenses.blUnlinked', 'Charge unlinked — removed from Bookinglayer.'),
      );
    },
    onError: (error: Error) => {
      notify.error(error.message, {
        description: t('bookingExpenses.blLinkFailed', 'Could not change the Bookinglayer link.'),
      });
    },
  });
}
